import { useState, type ReactNode } from 'react';
import { api } from '../../api/client';
import { usePagedLoader } from '../../api/paging';
import { ROLE_LABELS, type Page, type UserRole } from '../../api/types';
import { t } from '../../i18n';
import { docNumber } from '../../shared/lib/docNumber';
import {
  Action,
  Card,
  CardHeading,
  CardList,
  Code,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  Gap,
  Gutter,
  LoadingState,
  Meta,
  Muted,
  Section,
  SectionTitle,
  type CodeTone,
  type FilterOption,
} from '../../shared/ui';
import { ShowMore, formatDateTime, personName } from '../review/staffKit';

/**
 * Журнал действий администрации: смена ролей, снятие и возврат доступа,
 * правка персонажей, передача и настройки.
 *
 * Пишется сервером в одном месте и только на административные действия —
 * решения модераторов живут отдельно, в журнале решений на вкладке «Задачи».
 */

interface AuditRow {
  id: number;
  action: string;
  subjectType: string;
  subjectId: number | null;
  /** Снимки «до» и «после» в том виде, в каком их сложил сервер. */
  before: unknown;
  after: unknown;
  createdAt: string;
  actor: { id: number; vkUserId: number; displayName: string | null; role: UserRole };
}

/**
 * Карта держит и действия, которых код больше не пишет: журнал — это ИСТОРИЯ,
 * и строка, записанная год назад, обязана читаться словами. Так остался
 * `user.grant_slot` — слоты персонажей сняты вместе с колонкой, а строки о
 * них в базе остались.
 */
const ACTION_LABELS: Record<string, string> = {
  'user.create': t('audit.event.user.create'),
  'user.register': t('audit.event.user.register'),
  'user.update': t('audit.event.user.update'),
  'user.revoke': t('audit.event.user.revoke'),
  'user.restore': t('audit.event.user.restore'),
  'user.grant_slot': t('audit.event.user.grant_slot'),
  'character.update': t('audit.event.character.update'),
  'character.freeze': t('audit.event.character.freeze'),
  'character.archive': t('audit.event.character.archive'),
  'character.transfer': t('audit.event.character.transfer'),
  'character.delete': t('audit.event.character.delete'),
  'config.update': t('audit.event.config.update'),
  'user.bootstrap': t('audit.event.user.bootstrap'),
  'user.data_deletion': t('audit.event.user.data_deletion'),
};

/**
 * Значения обязаны совпадать с SubjectType на сервере
 * (apps/api/src/db/repositories/audit-log.repository.ts) буква в букву:
 * фильтр уходит в query как есть и сравнивается с колонкой напрямую.
 * Здесь стояли app_user, game_character и app_config — имена таблиц, которых
 * больше нет; фильтр молча возвращал пустую страницу, а у строк не находилось
 * подписи. Имущества персонажа здесь нет намеренно: предметы, питомцы и
 * способности в audit_log не пишут — их след это moderation_decision и
 * колонки архива на самой строке.
 */
const SUBJECT_LABELS: Record<string, string> = {
  user: t('audit.subject.user'),
  character: t('audit.subject.character'),
  setting: t('audit.subject.setting'),
};

type SubjectFilter = 'all' | 'user' | 'character' | 'setting';

const SUBJECT_FILTERS: readonly FilterOption<SubjectFilter>[] = [
  { value: 'all', label: t('common.empty.all') },
  { value: 'user', label: t('common.section.members') },
  { value: 'character', label: t('common.section.characters') },
  { value: 'setting', label: t('audit.filter.setting') },
];

export function AuditLogSection({ tabs, active }: { tabs: ReactNode; active: boolean }) {
  const [subjectType, setSubjectType] = useState<SubjectFilter>('all');
  const [openId, setOpenId] = useState<number | null>(null);

  const log = usePagedLoader<AuditRow>(
    (params, signal) =>
      api.get<Page<AuditRow>>(
        '/api/audit',
        {
          subjectType: subjectType === 'all' ? undefined : subjectType,
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      ),
    [subjectType],
    active,
  );

  return (
    <>
      {/* Фильтр — в секции навигации, сразу под рядом разделов админки. */}
      <Section>
        {tabs}
        <FilterBar>
          <FilterSelect
            label={t('audit.filter.subject')}
            options={SUBJECT_FILTERS}
            value={subjectType}
            onChange={setSubjectType}
          />
        </FilterBar>
      </Section>

      <Section>
        <SectionTitle accent="pink">{t('audit.title')}</SectionTitle>
        <Gutter>
          <Muted>{t('audit.hint')}</Muted>
        </Gutter>

        {log.loading && !log.data ? <LoadingState /> : null}
        {log.error ? <ErrorState message={log.error} onRetry={log.reload} /> : null}

        {log.data && log.data.items.length === 0 ? (
          <EmptyState title={t('audit.empty.title')}>{t('audit.empty.text')}</EmptyState>
        ) : null}

        <CardList>
          {log.data?.items.map((row) => (
            <Card key={row.id}>
              <CardHeading>{ACTION_LABELS[row.action] ?? row.action}</CardHeading>

              <Gap step="s1h" />
              <Meta>
                {[
                  SUBJECT_LABELS[row.subjectType] ?? row.subjectType,
                  row.subjectId === null ? null : docNumber(row.subjectId),
                ]}
              </Meta>

              <Gap step="s1" />
              {/* Тише первой строки: над чем действие, читают первым, а кто и
                когда — уже уточнение. Одним цветом обе сливались в абзац. */}
              <Meta tone="dim">
                {[
                  `${personName(row.actor)} (${ROLE_LABELS[row.actor.role]})`,
                  formatDateTime(row.createdAt),
                ]}
              </Meta>

              {openId === row.id ? (
                <>
                  <Snapshot label={t('audit.snapshot.before')} tone="neutral" value={row.before} />
                  <Snapshot label={t('audit.snapshot.after')} tone="accent" value={row.after} />
                </>
              ) : null}

              {/* Кнопка под снимком, а не над ним: раскрытая карточка кончается
                тем же местом, куда человек только что нажал. */}
              <Gap step="s2" />
              <Action
                tone="link"
                size="s"
                onClick={() => setOpenId(openId === row.id ? null : row.id)}
              >
                {openId === row.id ? t('audit.action.hideDetails') : t('audit.action.details')}
              </Action>
            </Card>
          ))}
        </CardList>

        <ShowMore page={log.data} onMore={log.loadMore} loading={log.loading} />
      </Section>
    </>
  );
}

/**
 * Снимок состояния как есть. Раскладывать его на человеческие подписи здесь
 * незачем: поля разные для каждого действия, а разбирается такое раз в год —
 * и тогда важнее увидеть ровно то, что записал сервер.
 *
 * Без отступов внутри JSON: строка «было» и строка «стало» стоят рядом, и
 * сравнивают их взглядом — а развёрнутый на восемь строк объект это ломает.
 */
function Snapshot({ label, tone, value }: { label: string; tone: CodeTone; value: unknown }) {
  if (value === null || value === undefined) return null;
  return (
    <>
      <Gap step="s1h" />
      <Code label={label} tone={tone}>
        {JSON.stringify(value)}
      </Code>
    </>
  );
}
